import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  obtenerBrandingTiendaSetup,
  actualizarBrandingTiendaSetup,
  subirLogoTiendaSetup,
  quitarLogoTiendaSetup,
} from "../../../services/usuarios_tienda/tiendaSetup";
import { fileURL } from "../../../utils/fileURL";
import {
  BrandingPreview,
  buildPreviewActiveKeys,
  parseBrandingResponse,
  seccionCodigoDe,
  getSectionDescription,
  BRANDING_FIELDS,
  TEXT_KEYS,
  COLOR_KEYS,
  isHexColorString,
  normalizeHexForPicker,
} from "./TabBranding";
import "../../../components/store/StoreHeader.css";
import "../../../components/store/Footer.css";
import "./TabBranding.css";
import "./DesignEditor.css";

const VOLVER_PATH = "/administracion/tienda/tienda";

const nombreSeccion = (code) => {
  if (code === "HEADER") return "Encabezado";
  if (code === "FOOTER") return "Pie de página";
  return "Sección";
};

// Hex con el que abre el selector nativo (acepta hex o rgba).
// IMPORTANTE: siempre en minúsculas. El <input type="color"> normaliza su valor
// a minúsculas; si le pasáramos un hex en mayúsculas, React detectaría un
// desajuste permanente entre el prop y el DOM y dispararía onChange en bucle
// infinito ("Maximum update depth exceeded").
const valorAHexPicker = (val) => {
  if (isHexColorString(val)) return normalizeHexForPicker(val).toLowerCase();
  const m = String(val).match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) {
    const toHex = (n) =>
      Math.max(0, Math.min(255, parseInt(n, 10))).toString(16).padStart(2, "0");
    return `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`.toLowerCase();
  }
  return "#888888";
};

// Aplica el hex elegido conservando la transparencia (alpha) si el valor era rgba.
const aplicarColorElegido = (current, hex) => {
  const m = String(current).match(
    /rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*([\d.]+))?\s*\)/i
  );
  if (m) {
    const a = m[1] != null ? m[1] : "1";
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return hex;
};

// Devuelve la opacidad (0..1) de un color. 1 si es hex o no tiene alpha.
const extraerAlpha = (val) => {
  const m = String(val).match(
    /rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([\d.]+)\s*\)/i
  );
  if (m) {
    const a = parseFloat(m[1]);
    return Number.isFinite(a) ? Math.max(0, Math.min(1, a)) : 1;
  }
  return 1;
};

// Aplica una opacidad (0..1) a un color conservando su RGB.
// Si queda totalmente opaco devuelve hex limpio; si no, rgba(...).
const aplicarAlpha = (current, alpha) => {
  const hex = valorAHexPicker(current);
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const a = Math.max(0, Math.min(1, alpha));
  if (a >= 1) return hex;
  return `rgba(${r}, ${g}, ${b}, ${Number(a.toFixed(2))})`;
};

// Extrae el color de una cadena de sombra (rgba/rgb/#hex).
const COLOR_RE = /rgba?\([^)]*\)|#[0-9a-fA-F]{3,8}/;
const extraerColorSombra = (s) => {
  const m = String(s).match(COLOR_RE);
  return m ? m[0] : "";
};
// Reemplaza solo el color dentro de la sombra, conservando posición/desenfoque.
const reemplazarColorSombra = (s, nuevoColor) => {
  if (COLOR_RE.test(s)) return String(s).replace(COLOR_RE, nuevoColor);
  return `${String(s).trim()} ${nuevoColor}`.trim();
};

export default function DesignEditorPage() {
  const navigate = useNavigate();

  const [workingRows, setWorkingRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [selectedSeccionId, setSelectedSeccionId] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const [search, setSearch] = useState("");
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const baselineRef = useRef({});
  const canvasRef = useRef(null);
  const logoInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    const res = await obtenerBrandingTiendaSetup();

    if (res.ok && res.data !== undefined) {
      const { workingRows: next, seccionesCatalogo } = parseBrandingResponse(res.data);
      const cat = Array.isArray(res.data.catalogo) ? res.data.catalogo : [];
      const vals = Array.isArray(res.data.valores) ? res.data.valores : [];
      const vmap = new Map(vals.map((v) => [Number(v.branding_variable_id), v.valor]));

      const catBySec = new Map();
      cat.forEach((v) => {
        const s = Number(v.seccion_branding_id);
        if (!catBySec.has(s)) catBySec.set(s, []);
        catBySec.get(s).push(v);
      });
      const seeded = next.map((row) => {
        const list = catBySec.get(Number(row.seccion_branding_id));
        if (!list || !list.length) return row;
        const r = { ...row };
        list.forEach((v) => {
          const propio = vmap.get(Number(v.branding_variable_id));
          r[v.clave] =
            propio != null && String(propio).trim() !== ""
              ? propio
              : v.valor_default ?? r[v.clave] ?? "";
        });
        return r;
      });

      const base = {};
      seeded.forEach((r) => {
        const snap = {};
        BRANDING_FIELDS.forEach(([k]) => {
          snap[k] = r[k] ?? "";
        });
        base[Number(r.seccion_branding_id)] = snap;
      });
      baselineRef.current = base;

      setLogoUrl(res.data.tienda?.url_logo ?? null);
      setCatalogo(cat);
      setWorkingRows(seeded);
      const firstId = next.length ? Number(next[0].seccion_branding_id) : null;
      const fallback = seccionesCatalogo.length && firstId != null ? firstId : null;
      setSelectedSeccionId((prev) =>
        prev != null && next.some((r) => Number(r.seccion_branding_id) === prev) ? prev : fallback
      );
    } else {
      setMsg({ type: "err", text: res.message });
      setWorkingRows([]);
      setSelectedSeccionId(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!workingRows.length) return;
    const ids = workingRows.map((r) => Number(r.seccion_branding_id));
    if (selectedSeccionId == null || !ids.includes(Number(selectedSeccionId))) {
      setSelectedSeccionId(ids[0]);
    }
  }, [workingRows, selectedSeccionId]);

  useEffect(() => {
    const grupos = catalogo
      .filter((v) => Number(v.seccion_branding_id) === Number(selectedSeccionId))
      .map((v) => v.grupo);
    setOpenGroups(new Set(grupos));
    setSearch("");
  }, [selectedSeccionId, catalogo]);

  const patchRow = (seccionId, field, value) => {
    setWorkingRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        if (Number(r.seccion_branding_id) !== Number(seccionId)) return r;
        if (r[field] === value) return r; // sin cambio real
        changed = true;
        return { ...r, [field]: value };
      });
      // Si el valor no cambió, devolvemos el mismo array: React aborta el
      // re-render y se evita cualquier bucle (p. ej. el input de color al
      // reconciliar un valor rgba dispara onChange repetidamente).
      return changed ? next : prev;
    });
  };

  const toggleGroup = (g) =>
    setOpenGroups((prev) => {
      const n = new Set(prev);
      if (n.has(g)) n.delete(g);
      else n.add(g);
      return n;
    });

  const restaurarGrupo = (vars) =>
    vars.forEach((v) => patchRow(selectedSeccionId, v.clave, v.valor_default ?? ""));

  const onElegirLogo = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ""; // permite re-subir el mismo archivo
    if (!file) return;
    setLogoBusy(true);
    setMsg(null);
    const res = await subirLogoTiendaSetup(file);
    if (res.ok) {
      setLogoUrl(res.data?.url_logo ?? null);
      setMsg({ type: "ok", text: res.message || "Logo actualizado." });
    } else {
      setMsg({ type: "err", text: res.message });
    }
    setLogoBusy(false);
  };

  const onQuitarLogo = async () => {
    if (!window.confirm("¿Quitar el logo y volver al predeterminado?")) return;
    setLogoBusy(true);
    setMsg(null);
    const res = await quitarLogoTiendaSetup();
    if (res.ok) {
      setLogoUrl(null);
      setMsg({ type: "ok", text: res.message || "Logo eliminado." });
    } else {
      setMsg({ type: "err", text: res.message });
    }
    setLogoBusy(false);
  };

  const selectedRow = workingRows.find(
    (r) => Number(r.seccion_branding_id) === Number(selectedSeccionId)
  );
  const code = selectedRow ? seccionCodigoDe(selectedRow) : "";

  const catalogoSel = useMemo(
    () =>
      catalogo
        .filter((v) => Number(v.seccion_branding_id) === Number(selectedSeccionId))
        .slice()
        .sort((a, b) => (a.orden || 0) - (b.orden || 0)),
    [catalogo, selectedSeccionId]
  );
  const usaCatalogo = catalogoSel.length > 0;
  const gruposCatalogo = useMemo(() => {
    const m = new Map();
    catalogoSel.forEach((v) => {
      if (!m.has(v.grupo)) m.set(v.grupo, []);
      m.get(v.grupo).push(v);
    });
    return Array.from(m.entries());
  }, [catalogoSel]);

  const isSearching = search.trim() !== "";
  const gruposFiltrados = useMemo(() => {
    if (!isSearching) return gruposCatalogo;
    const q = search.trim().toLowerCase();
    return gruposCatalogo
      .map(([g, vars]) => [
        g,
        vars.filter(
          (v) =>
            String(v.etiqueta).toLowerCase().includes(q) ||
            String(v.clave).toLowerCase().includes(q) ||
            String(g).toLowerCase().includes(q)
        ),
      ])
      .filter(([, vars]) => vars.length);
  }, [gruposCatalogo, isSearching, search]);

  const dirty = useMemo(() => {
    if (!selectedRow) return false;
    const base = baselineRef.current[Number(selectedSeccionId)] || {};
    return BRANDING_FIELDS.some(([k]) => (base[k] ?? "") !== (selectedRow[k] ?? ""));
  }, [selectedRow, selectedSeccionId]);

  const textFields = useMemo(() => BRANDING_FIELDS.filter(([k]) => TEXT_KEYS.has(k)), []);
  const colorFields = useMemo(() => BRANDING_FIELDS.filter(([k]) => COLOR_KEYS.has(k)), []);
  const shadowFields = useMemo(
    () => BRANDING_FIELDS.filter(([k]) => !TEXT_KEYS.has(k) && !COLOR_KEYS.has(k)),
    []
  );
  const previewActiveKeys = useMemo(
    () =>
      buildPreviewActiveKeys({
        usaCatalogo,
        catalogoSel,
        code,
        colorFields,
        shadowFields,
        textFields,
      }),
    [usaCatalogo, catalogoSel, code, colorFields, shadowFields, textFields]
  );

  const guardar = async () => {
    if (!selectedRow) return;
    setSaving(true);
    setMsg(null);

    const item = { seccion_branding_id: Number(selectedRow.seccion_branding_id) };
    BRANDING_FIELDS.forEach(([key]) => {
      item[key] = selectedRow[key];
    });
    const tid = Number(selectedRow.tienda_branding_id);
    if (Number.isInteger(tid) && tid > 0) item.tienda_branding_id = tid;

    const payload = { branding: [item] };
    if (catalogoSel.length) {
      payload.valores = catalogoSel.map((v) => ({
        branding_variable_id: v.branding_variable_id,
        valor: selectedRow[v.clave] ?? "",
      }));
    }

    const res = await actualizarBrandingTiendaSetup(payload);
    if (res.ok) {
      setMsg({ type: "ok", text: res.message || "Cambios guardados." });
      await load();
    } else {
      setMsg({ type: "err", text: res.message });
    }
    setSaving(false);
  };

  // Confirmar al salir si hay cambios sin guardar.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const salir = () => {
    if (dirty && !window.confirm("Tienes cambios sin guardar. ¿Salir de todas formas?")) return;
    navigate(VOLVER_PATH);
  };

  // Bloquea el scroll del body mientras el editor está abierto.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Pantalla completa real (Fullscreen API).
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Zoom-to-fit: escala el lienzo (diseñado a 1200px) para que el header se vea
  // completo y se adapte al ancho disponible (responsive en cualquier pantalla).
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const DISENO_ANCHO = 1200;
    const calc = () => {
      const disponible = el.clientWidth - 64; // descuenta el padding del lienzo
      const z = Math.min(1, Math.max(0.35, disponible / DISENO_ANCHO));
      setZoom(Number(z.toFixed(3)));
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [selectedRow, inspectorOpen, loading]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const renderField = (key, label, hint, resetTo) => {
    if (!selectedRow || selectedSeccionId == null) return null;
    const sid = selectedSeccionId;
    const val = selectedRow[key] ?? "";
    const puedeRestaurar = resetTo != null && val !== resetTo;

    const top = (
      <div className="de-field__top">
        <label htmlFor={`de-${key}`} className="de-field__label">
          {label}
        </label>
        {puedeRestaurar && (
          <button
            type="button"
            className="de-field__reset"
            onClick={() => patchRow(sid, key, resetTo)}
            title="Restaurar al valor por defecto"
          >
            ↺
          </button>
        )}
      </div>
    );

    if (COLOR_KEYS.has(key)) {
      return (
        <div className="de-field" key={key}>
          {top}
          <div className="de-color">
            <label
              className="de-swatch"
              style={{ background: val || "#ffffff" }}
              title="Elegir color"
            >
              <input
                type="color"
                value={valorAHexPicker(val)}
                onChange={(e) => patchRow(sid, key, aplicarColorElegido(val, e.target.value))}
                aria-label={`Selector de color: ${label}`}
              />
            </label>
            <input
              id={`de-${key}`}
              className="de-input"
              type="text"
              value={val}
              onChange={(e) => patchRow(sid, key, e.target.value)}
              placeholder="#RRGGBB o rgba(...)"
              spellCheck={false}
            />
          </div>
          <div className="de-opacity">
            <span className="de-opacity__label">Opacidad</span>
            <input
              type="range"
              className="de-opacity__range"
              min="0"
              max="100"
              step="1"
              value={Math.round(extraerAlpha(val) * 100)}
              onChange={(e) =>
                patchRow(sid, key, aplicarAlpha(val, Number(e.target.value) / 100))
              }
              aria-label={`Opacidad: ${label}`}
            />
            <span className="de-opacity__val">
              {Math.round(extraerAlpha(val) * 100)}%
            </span>
          </div>
          {hint && <span className="de-field__hint">{hint}</span>}
        </div>
      );
    }

    if (TEXT_KEYS.has(key)) {
      return (
        <div className="de-field" key={key}>
          <div className="de-field__top">
            <label htmlFor={`de-${key}`} className="de-field__label">
              {label}
            </label>
          </div>
          <input
            id={`de-${key}`}
            className="de-input"
            value={val}
            onChange={(e) => patchRow(sid, key, e.target.value)}
            placeholder={key === "slogan" ? "Escribe un slogan" : "Escribe un texto"}
          />
        </div>
      );
    }

    // Sombra: swatch que edita solo el color (conserva posición/desenfoque) + texto.
    const colorSombra = extraerColorSombra(val);
    return (
      <div className="de-field" key={key}>
        {top}
        <div className="de-color">
          <label
            className="de-swatch"
            style={{ background: colorSombra || "#ffffff" }}
            title="Color de la sombra"
          >
            <input
              type="color"
              value={valorAHexPicker(colorSombra)}
              onChange={(e) =>
                patchRow(sid, key, reemplazarColorSombra(val, aplicarColorElegido(colorSombra, e.target.value)))
              }
              aria-label={`Color de la sombra: ${label}`}
            />
          </label>
          <input
            id={`de-${key}`}
            className="de-input"
            value={val}
            onChange={(e) => patchRow(sid, key, e.target.value)}
            placeholder="0 12px 24px rgba(...)"
          />
        </div>
        {hint && <span className="de-field__hint">{hint}</span>}
      </div>
    );
  };

  return createPortal(
    <div className="de-root">
      {/* Barra de herramientas */}
      <header className="de-toolbar">
        <div className="de-toolbar__left">
          <button type="button" className="de-back" onClick={salir} title="Volver">
            ← Volver
          </button>
          <div className="de-title">Editor de diseño</div>
        </div>

        <div className="de-tabs" role="tablist" aria-label="Secciones">
          {workingRows.map((row) => {
            const sid = Number(row.seccion_branding_id);
            const c = seccionCodigoDe(row);
            const active = sid === Number(selectedSeccionId);
            return (
              <button
                key={sid}
                type="button"
                role="tab"
                aria-selected={active}
                className={`de-tab${active ? " is-active" : ""}`}
                onClick={() => setSelectedSeccionId(sid)}
              >
                {String(row.seccion_nombre || "").trim() || nombreSeccion(c) || c}
              </button>
            );
          })}
        </div>

        <div className="de-toolbar__right">
          <span className={`de-status${dirty ? " is-dirty" : ""}`}>
            {dirty ? "Cambios sin guardar" : "Guardado"}
          </span>
          {msg && (
            <span className={`de-msg de-msg--${msg.type === "ok" ? "ok" : "err"}`}>{msg.text}</span>
          )}
          <button
            type="button"
            className={`de-icon-btn${inspectorOpen ? " is-on" : ""}`}
            onClick={() => setInspectorOpen((v) => !v)}
            title={inspectorOpen ? "Ocultar panel" : "Mostrar panel"}
          >
            ▥
          </button>
          <button
            type="button"
            className="de-icon-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
          >
            {isFullscreen ? "⤡" : "⤢"}
          </button>
          <button
            type="button"
            className="de-save"
            onClick={guardar}
            disabled={saving || (usaCatalogo && !dirty)}
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </header>

      {/* Cuerpo: lienzo + inspector */}
      <div className={`de-body${inspectorOpen ? "" : " de-body--collapsed"}`}>
        <main className="de-canvas" ref={canvasRef}>
          {loading ? (
            <div className="de-loading">Cargando…</div>
          ) : !selectedRow ? (
            <div className="de-loading">No hay secciones de branding.</div>
          ) : (
            <div className="de-artboard" style={{ zoom }}>
              <div className="tsb-preview-surface">
                <BrandingPreview
                  row={selectedRow}
                  logoUrl={logoUrl}
                  activeKeys={previewActiveKeys}
                />
              </div>
            </div>
          )}
        </main>

        {inspectorOpen && selectedRow && (
          <aside className="de-inspector">
            <div className="de-inspector__head">
              <h3>{nombreSeccion(code)}</h3>
              <p>{getSectionDescription(code)}</p>
            </div>

            {usaCatalogo && (
              <div className="de-search">
                <span className="de-search__icon" aria-hidden>⌕</span>
                <input
                  className="de-search__input"
                  placeholder="Buscar variable…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {isSearching && (
                  <button type="button" className="de-search__clear" onClick={() => setSearch("")} title="Limpiar">
                    ✕
                  </button>
                )}
              </div>
            )}

            <div className="de-inspector__scroll">
              {(code === "HEADER" || code === "FOOTER") && (
                <section className="de-logo">
                  <div className="de-logo__head">
                    <span className="de-logo__title">Logo / Imagen</span>
                  </div>
                  <div className="de-logo__row">
                    <div className="de-logo__thumb">
                      <img
                        src={fileURL(logoUrl) || "/image/cyravi-default-logo.png"}
                        alt="Logo de la tienda"
                        onError={(e) => {
                          e.currentTarget.src = "/image/cyravi-default-logo.png";
                        }}
                      />
                    </div>
                    <div className="de-logo__actions">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        hidden
                        onChange={onElegirLogo}
                      />
                      <button
                        type="button"
                        className="de-logo__btn"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoBusy}
                      >
                        {logoBusy ? "Subiendo…" : logoUrl ? "Cambiar imagen" : "Subir imagen"}
                      </button>
                      {logoUrl && (
                        <button
                          type="button"
                          className="de-logo__btn de-logo__btn--ghost"
                          onClick={onQuitarLogo}
                          disabled={logoBusy}
                        >
                          Quitar
                        </button>
                      )}
                      <span className="de-logo__hint">PNG, JPG o WEBP. Recomendado PNG con fondo transparente.</span>
                    </div>
                  </div>
                </section>
              )}

              {usaCatalogo ? (
                gruposFiltrados.length === 0 ? (
                  <p className="de-empty">No hay variables que coincidan con “{search}”.</p>
                ) : (
                  gruposFiltrados.map(([grupo, vars]) => {
                    const abierto = isSearching || openGroups.has(grupo);
                    return (
                      <section className={`de-grp${abierto ? " is-open" : ""}`} key={grupo}>
                        <header className="de-grp__head">
                          <button
                            type="button"
                            className="de-grp__toggle"
                            onClick={() => toggleGroup(grupo)}
                            aria-expanded={abierto}
                          >
                            <span className="de-grp__chev" aria-hidden>▸</span>
                            <span className="de-grp__name">{grupo}</span>
                            <span className="de-grp__count">{vars.length}</span>
                          </button>
                          <button
                            type="button"
                            className="de-grp__reset"
                            onClick={() => restaurarGrupo(vars)}
                            title="Restaurar este grupo a sus valores por defecto"
                          >
                            Restaurar
                          </button>
                        </header>
                        {abierto && (
                          <div className="de-grp__body">
                            {vars.map((v) =>
                              renderField(v.clave, v.etiqueta, v.descripcion, v.valor_default)
                            )}
                          </div>
                        )}
                      </section>
                    );
                  })
                )
              ) : (
                <>
                  <section className="de-grp is-open">
                    <header className="de-grp__head de-grp__head--static">
                      <span className="de-grp__name">Colores</span>
                      <span className="de-grp__count">{colorFields.length}</span>
                    </header>
                    <div className="de-grp__body">
                      {colorFields.map(([key, label]) => renderField(key, label))}
                    </div>
                  </section>
                  <section className="de-grp is-open">
                    <header className="de-grp__head de-grp__head--static">
                      <span className="de-grp__name">Sombras</span>
                      <span className="de-grp__count">{shadowFields.length}</span>
                    </header>
                    <div className="de-grp__body">
                      {shadowFields.map(([key, label]) => renderField(key, label))}
                    </div>
                  </section>
                </>
              )}

              {/* Contenido (slogan/texto): no editable en header ni footer desde el editor. */}
              {code !== "HEADER" && code !== "FOOTER" && (
                <section className="de-grp is-open">
                  <header className="de-grp__head de-grp__head--static">
                    <span className="de-grp__name">Contenido</span>
                    <span className="de-grp__count">{textFields.length}</span>
                  </header>
                  <div className="de-grp__body">
                    {textFields.map(([key, label]) => renderField(key, label))}
                  </div>
                </section>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>,
    document.body
  );
}
